import { useMutation } from '@tanstack/react-query';
import { Form, Input, InputRef, Modal, Space } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { SaleTeamDetailApi } from 'apis/SaleTeamDetailApi';
import { EditGrayIcon, EditIcon, User3Icon, UserIconV2 } from 'assets/svg';
import { ButtonAction, SelectForm, Switch } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import {
  GetTeamSales,
  IGetManagerCombobox,
  IGetSlocByPlant,
  IGetUserByPlantNotTeamSale,
  ISloc,
  ITeamManager,
  Item,
  PageInfo,
  STATUS_NAME_ST,
  STATUS_TS,
  TteamSalesDetail,
  UserNotTS,
} from 'interfaces/TeamSale/TeamSale';
import { IParamsGetAllTeamSalesDetail } from 'interfaces/TeamSaleDetail/TeamSaleDetail';
import Content from 'layouts/Content';
import { HocChangePagination } from 'utils/HocChangePagination';
import { TEAM_TYPE_OPTIONS } from 'utils/constants/TeamSale/TeamSale';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { formatDate, toastError } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import DrawerCustomerTS from './DrawerCustomerTS';
import DrawerSaleTeamPage from './DrawerSaleTeamPage';

interface IProps {
  loading: boolean;
  isAddRow: boolean;
  filterRef?: any;
  onSaveRow(record: Item, form: any, index: number): void;
  onSwitchRow(record: any): void;
  setIsAddRow: React.Dispatch<React.SetStateAction<boolean>>;
  data: {
    value: GetTeamSales | null;
    setValue: React.Dispatch<React.SetStateAction<GetTeamSales>>;
  };
  edit: {
    value: number;
    setValue: React.Dispatch<React.SetStateAction<number>>;
  };
  getTeamSales({
    afterMutation,
  }: {
    afterMutation?(items: Item[]): void;
  }): void;
}

export interface IOpenDrawer {
  isEmployee: boolean;
  isCustomer: boolean;
}

const { SaveTwoTone, CloseCircleTwoTone, ExclamationCircleFilled } = icons;
const newData = { statusId: STATUS_TS.active };

const EMPLOYESS_NOT_TEAM_SALE_INIT = {
  items: [],
  pageInfo: {} as PageInfo,
} as {
  items: UserNotTS[] | undefined;
  pageInfo: PageInfo;
};

const TableSaleTeamPage = forwardRef(
  (
    {
      data,
      edit,
      isAddRow,
      onSaveRow,
      onSwitchRow,
      getTeamSales,
      setIsAddRow,
      ...props
    }: IProps,
    ref: any,
  ) => {
    const { confirm } = Modal;

    const { user } = useUser(),
      [form] = Form.useForm(),
      navigate = useNavigate();

    const inputRef = useRef<InputRef>(null);

    const [isTypedName, setIsTypedName] = useState(false),
      [currentTeam, setCurrentTeam] = useState(null),
      [openDrawer, setOpenDrawer] = useState<IOpenDrawer>({
        isEmployee: false,
        isCustomer: false,
      }),
      [employeesNotTeamSale, setEmployeesNotTeamSale] = useState(
        EMPLOYESS_NOT_TEAM_SALE_INIT,
      ),
      [slocsData, setSlocData] = useState<DefaultOptionType[]>(
        [] as DefaultOptionType[],
      ),
      [employees, setEmployees] = useState([] as TteamSalesDetail[]),
      [slocs, setSlocs] = useState<ILazySelectData>({
        options: [],
        totalItems: 0,
      }),
      [teamCodeObj, setTeamCodeObj] = useState({
        stay: undefined,
        store: undefined,
      }),
      [teamManagers, setTeamManagers] = useState<ILazySelectData>({
        options: [] as ITeamManager[],
        totalItems: 0,
      }),
      [currentTeamSalesDetail, setCurrentTeamSalesDetail] = useState<
        TteamSalesDetail[]
      >([]),
      [currentEmployeeStr, setCurrentEmployeeStr] = useState('');

    const queryParam = useQueryParam(),
      page = parseInt(queryParam.get('page') + '') || 1,
      pageSize =
        parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

    const { isPending: isGetEmployeesLoading, mutate: getUserByPlantId } =
        useMutation({
          mutationFn: (params: IGetUserByPlantNotTeamSale) =>
            SaleTeamApi.getUserByPlantId(params),
          onSuccess: (res) => {
            if (res.data?.getUser) {
              let currentEmployees: { id: string; fullName: string }[] = [];
              const employeeStr = form.getFieldValue('employees');

              if (
                currentEmployeeStr.trim() !== '-' ||
                (typeof employeeStr === 'string' && employeeStr.trim() !== '-')
              ) {
                currentEmployees = (
                  typeof employeeStr === 'string'
                    ? employeeStr
                    : currentEmployeeStr
                )
                  .split(',')
                  .map((item: string) => {
                    const [id, fullName] = item.trim().split('-');

                    return { id: id?.trim(), fullName: fullName?.trim() };
                  });
              }

              if (typeof employeeStr === 'string')
                form.setFieldValue(
                  'employees',
                  currentEmployees.map((item) => item?.id),
                );

              setEmployeesNotTeamSale({
                pageInfo: {
                  ...res.data.getUser?.pageInfo,
                  totalItems:
                    (res.data.getUser?.pageInfo?.totalItems ?? 0) +
                    currentEmployees.length,
                },
                items: [
                  ...new Set([
                    ...(res.data.getUser?.items ?? []),
                    ...currentEmployees,
                  ]),
                ] as UserNotTS[],
              });
            }
          },
          onError: (error) => {
            console.log(error);
          },
        }),
      { isPending: isGetManagersPending, mutate: getManagersCombobox } =
        useMutation({
          mutationFn: (params: IGetManagerCombobox) =>
            SaleTeamApi.getManagersCombobox(params),
          onSuccess: (res) => {
            const data = res.data?.getUser;

            if (!data) return;

            setTeamManagers({
              totalItems: data?.pageInfo?.totalItems ?? 0,
              options: (data?.items ?? ([] as ITeamManager[])).map(
                (item: any) => ({
                  label: `${item.email} - ${item.fullName}`,
                  value: item.id,
                }),
              ) as DefaultOptionType[],
            });
          },
          onError: (error: any) => {
            toastError(error);
          },
        }),
      { isPending: isGetSlocByPlantId, mutate: getSlocByPlantId } = useMutation(
        {
          mutationFn: (params: IGetSlocByPlant) =>
            SaleTeamApi.getSlocByPlantId(params),
          onSuccess: (res) => {
            if (res.data?.getSloc) {
              setSlocs({
                options: res.data?.getSloc.items?.map(
                  (item: ISloc) =>
                    ({
                      value: item?.id ?? '',
                      label: item?.id ?? '',
                    }) as DefaultOptionType,
                ),
                totalItems: res.data?.getSloc?.pageInfo?.totalItems ?? 0,
              });
            }
          },
          onError: (error) => {
            console.log(error);
          },
        },
      ),
      { mutate: getAllTeamSaleDetailsById } = useMutation({
        mutationFn: (params: IParamsGetAllTeamSalesDetail) =>
          SaleTeamDetailApi.getAllById(params),
        onSuccess: (res) => {
          if (res?.data?.getTeamSalesDetail) {
            setEmployees(res?.data?.getTeamSalesDetail.items);
            setCurrentTeamSalesDetail(res?.data?.getTeamSalesDetail.items);
          }
        },
        onError: (errors) => {
          console.log(errors);
        },
      }),
      {
        mutate: checkExistRouteOfSaleTeam,
        isPending: isCheckExistRouteOfSaleTeamPending,
      } = useMutation({
        mutationFn: ({
          teamSaleId,
        }: {
          teamSaleId: string;
          onSuccess: (items: any) => void;
        }) => SaleTeamApi.checkExistRouteOfSaleTeam(teamSaleId),
        onSuccess: (res, variables) => {
          if (res?.data?.getTeamSalesRoute)
            variables.onSuccess(res?.data?.getTeamSalesRoute?.items);
        },
      }),
      {
        mutate: handleCheckUserActiveRouteInTeam,
        isPending: isCheckUserActiveRouteInTeamPending,
      } = useMutation({
        mutationFn: (record: any) =>
          SaleTeamApi.checkUserActiveRouteInTeam({
            plantId: record?.plantId,
            teamSalesSubId: record?.subId,
          }),
        onSuccess: (res: any, variables) => {
          if (res.data) {
            const result = res?.data?.CheckUserActiveRouteInTeam;

            if (!result?.success) {
              const conflictData = result?.conflictData;

              toastError(`
                Kích hoạt đội thất bại do thành viên đã ở trong đội khác. 
                Thành viên ${conflictData?.map((item: any) => item?.user).join(', ')}
                đang ở trong đội ${[...new Set(conflictData?.map((item: any) => item?.team))].join(', ')}.
              `);
            } else handleSwitchStatusSaleTeam(variables);

            return;
          }
          if (res?.errors) toastError(res?.errors[0].message);
        },
        onError: (error) => {
          toastError(error?.message);
        },
      });

    useEffect(() => {
      if (employees.length === 0) form.setFieldValue('leader', undefined);
    }, [employees]);

    useEffect(() => {
      if (!isAddRow) return;

      const { teamTypeName } = form.getFieldsValue();
      const teamCode =
        teamTypeName === 'Tại chỗ' ? teamCodeObj.stay : teamCodeObj.store;

      form.setFieldsValue({
        id: teamCode,
        name: `${typeof teamCode === 'undefined' ? '' : teamCode}${employees.length > 0 ? ' - ' : ''}${employees
          .map((item) => {
            const nameSplit = item.user.fullName.split(' ');

            return nameSplit[nameSplit.length - 1];
          })
          .join('_')}`,
      });
    }, [teamCodeObj]);

    useEffect(() => {
      if (edit) {
        inputRef.current?.focus({ cursor: 'start' });
      }
    }, [data]);

    const handleOpenDrawEmployee = (record: any) => {
      setOpenDrawer((prev: IOpenDrawer) => ({
        ...prev,
        isEmployee: !prev.isEmployee,
      }));
      setCurrentTeam(record);
    };

    const handleOpenDrawerCustomer = (record: any) => {
      setOpenDrawer((prev: IOpenDrawer) => ({
        ...prev,
        isCustomer: !prev.isCustomer,
      }));
      setCurrentTeam(record);
    };

    const handlePlantIdChange = (_: any, option: any) => {
      form.setFieldValue('plantId', option.label);
    };

    const handleTypeChange = (_: any, option: any) => {
      try {
        if (!option) return;

        form.setFieldsValue({
          teamTypeId: option.value,
          teamTypeName: option.label,
        });
      } catch (error) {
        console.log(error);
      }
    };

    const handleSlocChange = (option: any) => {
      form.setFieldValue('sloc', option);
      setSlocData(option.map((item: string) => ({ label: item, value: item })));
    };

    const formatValue = (value: string) => {
      return value === '' || value === null || typeof value === 'undefined'
        ? '-'
        : value;
    };

    // const validatorMaxEmployees = (_: any, value: any) => {
    //   if (value && value.length > 4)
    //     return Promise.reject('Tối đa có 4 thành viên trong đội');
    //   else return Promise.resolve();
    // };

    const handleOnEmployeeDeSelect = (value: string) => {
      if (form.getFieldValue('leader')?.split('-')[0].trim() === value)
        form.setFieldValue('leader', undefined);
    };

    const handleEmployeesChange = (value: any) => {
      try {
        if (typeof employeesNotTeamSale.items === 'undefined') return;

        const newEmployees = employeesNotTeamSale.items
          .filter((employee) =>
            value.some((employeeId: string) => employeeId === employee.id),
          )
          .map(
            (employee) =>
              ({
                user: {
                  id: employee.id,
                  fullName: employee.fullName,
                  sloc: employee.sloc,
                },
              }) as TteamSalesDetail,
          );

        setEmployees(newEmployees);

        if (isTypedName) return;

        const teamCode =
          form.getFieldValue('teamTypeName') === 'Tại chỗ'
            ? teamCodeObj.stay
            : teamCodeObj.store;

        isAddRow &&
          form.setFieldsValue({
            name: `${typeof teamCode === 'undefined' ? '' : `${teamCode} - `}${newEmployees
              .map((item) => {
                const nameSplit = item.user.fullName.split(' ');

                return nameSplit[nameSplit.length - 1];
              })
              .join('_')}`,
          });
      } catch (error) {
        console.log(error);
      }
    };

    const handleTeamLeaderChange = (value: any) => {
      const leader = employees.find(
        (item: TteamSalesDetail) => item.user.id === value,
      );

      if (typeof leader === 'undefined') {
        form.setFieldValue('leader', undefined);
        return;
      }

      form.setFieldValue(
        'leader',
        `${leader?.user.id} - ${leader?.user.fullName}`,
      );
    };

    const handleSlocScrollChange = useCallback(
      (params: ILazySelectParams) => {
        if (!user) return;

        const { pageSize, value } = params;
        const plantId = form.getFieldValue('plantId');

        getSlocByPlantId({
          plantId: plantId,
          searchValue: value,
          page: 1,
          pageSize,
        });
      },
      [user],
    );

    const handleUserScrollChange = useCallback(
      (params: ILazySelectParams) => {
        if (!user) return;

        const { pageSize, value } = params;
        const plantId = user.isAdmin
          ? form.getFieldValue('plantId')
          : user.plantCode;

        getUserByPlantId({
          plantId: plantId,
          searchValue: value,
          page: 1,
          pageSize: pageSize,
        });
      },
      [user],
    );

    const handleTeamManagerScrollChange = useCallback(
      (params: ILazySelectParams) => {
        if (!user) return;
        if (user.isSale) return;

        const { pageSize, value } = params;

        getManagersCombobox({
          plantId: user.isCN ? user.plantCode : form.getFieldValue('plantId'),
          searchValue: value,
          pageSize: pageSize,
        });
      },
      [user],
    );

    const handleTeamNameChange = () => {
      if (isTypedName) return;

      setIsTypedName(true);
    };

    const columns: ColumnsTypeCustom = [
      {
        title: 'STT',
        width: 50,
        className: 'text-end align-top',
        align: 'center',
        render: (_: any, __: any, index: number) =>
          index + 1 + pageSize * (page - 1),
      },
      {
        title: 'Chi nhánh',
        dataIndex: 'plantId',
        className: 'text-start align-top',
        width: 80,
        align: 'center',
        render: (value: any, record: any, index: number) =>
          index == edit.value ? (
            <Form.Item
              name="plantId"
              rules={[{ required: true, message: 'Chọn chi nhánh' }]}
              className="m-0"
            >
              <SelectForm
                size="middle"
                className="max-h-[31.6px]"
                allowClear={false}
                placeholder="Chọn chi nhánh"
                disabled={true}
                onChange={(_, option) => handlePlantIdChange(record, option)}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          ),
      },
      {
        title: 'Loại đội',
        dataIndex: 'teamTypeName',
        width: 110,
        className: 'text-start align-top',
        render: (value: any, _: any, index: number) => {
          return index == edit.value ? (
            <Form.Item
              name={'teamTypeName'}
              rules={[{ required: true, message: 'Chọn loại đội' }]}
              className="m-0"
            >
              <SelectForm
                showSearch
                className="max-h-[31.6px]"
                size="middle"
                placeholder="Chọn loại đội"
                optionFilterProp="children"
                disabled={!isAddRow}
                options={TEAM_TYPE_OPTIONS}
                onChange={handleTypeChange}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Mã đội',
        dataIndex: 'subId',
        className: 'text-start align-top',
        width: 100,
        render: (value, _, __) => formatValue(value),
      },
      {
        width: 150,
        title: 'Sloc',
        dataIndex: 'sloc',
        className: 'text-start align-top',
        render: (value, _, index) => {
          return index == edit.value ? (
            <Form.Item
              name="sloc"
              className="m-0"
              rules={[{ required: true, message: 'Chọn sloc' }]}
            >
              <LazySelectForm
                showSearch
                mode="multiple"
                size="middle"
                placeholder="Chọn sloc"
                data={slocs}
                loading={isGetSlocByPlantId}
                onChange={handleSlocChange}
                onSelectScrollChange={handleSlocScrollChange}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Tên đội',
        dataIndex: 'name',
        width: 150,
        className: 'text-start align-top',
        render: (value: any, _: any, index: number) =>
          index == edit.value ? (
            <Form.Item
              name={'name'}
              rules={[{ required: true, message: 'Nhập tên đội' }]}
              className="m-0"
            >
              <Input
                placeholder="Nhập tên đội"
                value={value}
                allowClear
                onChange={handleTeamNameChange}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          ),
      },
      {
        title: 'Thành viên',
        dataIndex: 'employees',
        width: 250,
        className: 'text-start align-top',
        render: (value, _, index) => {
          return edit.value === index &&
            typeof employeesNotTeamSale.items !== 'undefined' ? (
            <Form.Item
              name={'employees'}
              // rules={[{ validator: validatorMaxEmployees }]}
              className="m-0"
            >
              <LazySelectForm
                mode="multiple"
                size="middle"
                placeholder="Chọn thành viên"
                data={{
                  options: employeesNotTeamSale.items.map((item: any) => {
                    const employee = `${item.id} - ${item.fullName}`;
                    return { label: employee, value: item.id };
                  }),
                  totalItems: employeesNotTeamSale?.pageInfo?.totalItems ?? 0,
                }}
                loading={isGetEmployeesLoading}
                onDeselect={handleOnEmployeeDeSelect}
                onChange={handleEmployeesChange}
                onSelectScrollChange={handleUserScrollChange}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Đội trưởng',
        dataIndex: 'leader',
        width: 250,
        className: 'text-start align-top',
        render: (value, _, index) => {
          return edit.value === index ? (
            <Form.Item name={'leader'} className="m-0">
              <SelectForm
                showSearch
                placeholder="Chọn đội trưởng"
                className="max-h-[31.6px]"
                size="middle"
                options={employees.map((item: TteamSalesDetail) => {
                  return {
                    label: `${item.user.id} - ${item.user.fullName}`,
                    value: item.user.id,
                  };
                })}
                onChange={(value) => handleTeamLeaderChange(value)}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Năng lực đội (khách)',
        dataIndex: 'teamCapacity',
        className: 'text-start align-top',
        width: 130,
        render: (value, _, index) => {
          return index == edit.value ? (
            <Form.Item
              name="teamCapacity"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number(value) >= 1 && Number(value) <= 99)
                      return Promise.resolve();
                    return Promise.reject(
                      new Error(
                        'Nhập năng lực đội tối thiểu là 1 và tối đa là 99',
                      ),
                    );
                  },
                },
              ]}
              className="m-0"
            >
              <Input placeholder="Nhập năng lực đội" type="number" />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Quản lý',
        dataIndex: 'manageName',
        width: 250,
        isShowRender: true,
        className: 'text-start align-top',
        render: (value, _, index) => {
          return edit.value === index ? (
            // (user?.isAdmin || (user?.isCN && !user.isQLD)) ? (
            <Form.Item name={'manageId'} className="m-0">
              <LazySelectForm
                size="middle"
                placeholder="Chọn quản lý"
                disabled={isAddRow && user?.isQLD}
                data={teamManagers}
                loading={isGetManagersPending}
                onSelectScrollChange={handleTeamManagerScrollChange}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Công ty phát hành',
        dataIndex: 'companyId',
        width: 150,
        align: 'center',
        className: 'text-start align-top',
        render: (value) => {
          return formatValue(value);
        },
      },
      {
        title: 'Trạng thái',
        dataIndex: 'statusName',
        width: 150,
        className: 'text-start align-top',
        isShowRender: true,
      },
      {
        title: 'Người tạo',
        dataIndex: 'createName',
        className: 'text-start align-top',
        width: 125,
        render: (value) => {
          return formatValue(value);
        },
      },
      {
        title: 'Ngày tạo',
        width: 160,
        dataIndex: 'createDate',
        className: 'text-start align-top',
        type: 'date',
        render: (value) => {
          return value && formatDate(value);
        },
      },
      {
        title: 'Người cập nhật',
        width: 125,
        dataIndex: 'modifyName',
        className: 'text-start align-top',
        render: (value) => {
          return formatValue(value);
        },
      },
      {
        title: 'Ngày cập nhật',
        width: 160,
        dataIndex: 'modifyDate',
        className: 'text-start align-top',
        type: 'date',
        render: (value) => {
          return value && formatDate(value);
        },
      },
      {
        fixed: 'right',
        align: 'center',
        width: 150,
        render: (_: any, record: any, index: number) =>
          edit.value == index ? (
            <Space size={'middle'} className="text-lg ">
              <ButtonAction
                tooltip="Lưu"
                onClick={() => handleSave(record, index)}
              >
                <SaveTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction tooltip="Huỷ" onClick={handleCancel}>
                <CloseCircleTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ) : (
            <Space size={'middle'} className="text-lg ">
              {!user?.isHO && (
                <ButtonAction
                  tooltip="Chỉnh sửa"
                  disabled={
                    edit.value !== -1 ||
                    record.statusName === STATUS_NAME_ST.DEACTIVE
                  }
                  onClick={() => handleEdit(record, index)}
                >
                  {edit.value === -1 ? <EditIcon /> : <EditGrayIcon />}
                </ButtonAction>
              )}
              <ButtonAction
                onClick={() => handleOpenDrawEmployee(record)}
                tooltip="Thành viên đội"
                disabled={edit.value !== -1}
              >
                <UserIconV2 />
              </ButtonAction>
              <ButtonAction
                onClick={() => handleOpenDrawerCustomer(record)}
                tooltip="Khách hàng"
                disabled={edit.value !== -1}
              >
                <User3Icon />
              </ButtonAction>
              {!user?.isHO && (
                <Switch
                  disabled={edit.value !== -1}
                  value={record.statusName === STATUS_NAME_ST.ACTIVE}
                  onChange={() => handleSwitchClick(record)}
                />
              )}
            </Space>
          ),
      },
    ];

    useImperativeHandle(ref, () => ({
      handleAddRow,
      handleExportExcel,
      handleClear,
      replaceData,
      getHeaderRow,
    }));

    const getHeaderRow = () => {
      columns.shift();
      columns.pop();
      return columns;
    };

    function handleExportExcel() {
      const newData = data?.value?.items.map((item) => {
        const currentLeader = item.tteamSalesDetails
          .filter((member: any) => member?.leaderYn === false)
          .map((member) => `${member.id} - ${member.user.fullName}`)
          .join('\n');

        return {
          ...item,
          tteamSalesDetails: currentLeader,
        };
      });

      exportExcel_v2(columns, newData, 'DS_Doi_Ban_Hang');
    }

    function handleAddRow() {
      form.resetFields();
      setEmployees([]);

      if (typeof user !== 'undefined') {
        const { isQLD, isCN, isSale, plantCode } = user;

        if (isQLD)
          form.setFieldsValue({
            plantId: plantCode,
            manageId: user.id,
            manageName: `${user.email} - ${user.fullName}`,
          });

        if (isCN || isSale) {
          form.setFieldsValue({ plantId: plantCode });
          getUserByPlantId({ plantId: plantCode });
        }
      }

      setIsAddRow(true);

      if (page === 1) {
        data.setValue((prev: any) => ({
          ...prev,
          items: [newData, ...prev.items],
        }));
        edit.setValue(0);
        return;
      }

      navigate(`?page=1&page_size=${pageSize}`);
      edit.setValue(0);
    }

    const replaceData = () => {
      const newData: Item[] = [...(data?.value?.items ?? [])];
      newData.shift();

      data.setValue((prev) => ({ ...prev, items: newData }));
    };

    const checkFormValidate = async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    };

    const handleClear = () => {
      edit.setValue(-1);
      form.resetFields();

      setEmployees([]);
      setTeamCodeObj({ stay: undefined, store: undefined });
      setEmployeesNotTeamSale((prev) => ({ ...prev, items: [] }));
      setSlocData([]);
      setCurrentEmployeeStr('');
    };

    const handleEdit = async (record: any, index: number) => {
      edit.setValue(index);

      setCurrentEmployeeStr(record?.employees);

      getAllTeamSaleDetailsById({ teamSaleId: record?.id, useYn: true });

      form.setFieldsValue({
        ...record,
        sloc: record.sloc?.split(';').map((item: any) => {
          const value = item.trim();
          return { label: value, value: value } as DefaultOptionType;
        }),
      });

      setSlocData(
        record.sloc?.split(';').map((item: any) => {
          const value = item.trim();
          return { label: value, value: value } as DefaultOptionType;
        }),
      );
    };

    const handleSave = async (record: any, index: number) => {
      if (!(await checkFormValidate()) || typeof user === 'undefined') return;

      onSaveRow(
        {
          ...record,
          slocsData,
          employees,
          isTypedName,
          currentEmployees: currentTeamSalesDetail,
        },
        form.getFieldsValue(),
        index,
      );

      edit.setValue(-1);
      setIsTypedName(false);
    };

    const handleCancel = () => {
      edit.value == 0 &&
        !data?.value?.items[0]?.id &&
        data.setValue((prev) => ({
          ...prev,
          items: data?.value?.items.slice(1) || [],
        }));

      setIsAddRow(false);
      setIsTypedName(false);
      handleClear();
    };

    const handleSwitchStatusSaleTeam = (record: any) => {
      onSwitchRow(record);
    };

    const handleSwitchClick = (record?: any) => {
      try {
        if (typeof user === 'undefined') return;

        if (record.statusName === STATUS_NAME_ST.ACTIVE) {
          checkExistRouteOfSaleTeam({
            teamSaleId: record?.id,
            onSuccess: (items) => {
              if (items.length > 0) {
                confirm({
                  title: `Đội ${record?.subId} đang trong ${items.length} tuyến bán hàng 
                  ${items.map((item: any) => item?.troute?.id).join(', ')}.
                  Bạn có chắc muốn ngưng hoạt động đội này không?`,
                  icon: <ExclamationCircleFilled />,
                  onOk: () => {
                    handleSwitchStatusSaleTeam(record);
                    // disableManySalesTeamRoute(
                    //   (items ?? []).map((item: any) => ({
                    //     id: item?.id,
                    //     useYn: false,
                    //   })),
                    // );
                  },
                });
                return;
              }

              handleSwitchStatusSaleTeam(record);
            },
          });
          return;
        } else handleCheckUserActiveRouteInTeam(record);
      } catch (error) {
        console.log(error);
      }
    };

    return (
      <Content>
        <Form form={form}>
          <TableComponent
            columns={columns}
            dataSource={data?.value?.items}
            rowKey={(record: any) => record?.id ?? ''}
            loading={
              props.loading ||
              isCheckExistRouteOfSaleTeamPending ||
              isCheckUserActiveRouteInTeamPending
            }
            scroll={{ x: 3100 }}
            pagination={{
              disabled: edit.value !== -1,
              current: data?.value?.pageInfo?.currentPage || 1,
              pageSize:
                data?.value?.pageInfo?.itemsPerPage || DEFAULT_PAGE_SIZE,
              total: data?.value?.pageInfo?.totalItems,
              onChange: HocChangePagination(),
            }}
          />
        </Form>
        <DrawerSaleTeamPage
          getTeamSales={getTeamSales}
          currentTeam={currentTeam}
          setCurrentTeam={setCurrentTeam}
          open={openDrawer.isEmployee}
          setOpen={setOpenDrawer}
        />
        <DrawerCustomerTS
          currentTeam={currentTeam}
          open={openDrawer.isCustomer}
          setOpen={setOpenDrawer}
        />
      </Content>
    );
  },
);

export default TableSaleTeamPage;
